import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { QueryStatus } from '@adv-frontend/modules-query';
import { type OpenApiSchemaCategory } from '#shared/api/schema';
import { useCheckAsync } from '#shared/lib/hooks';
import { api } from '../api/api';
import { createCategoryMap } from '../lib/createCategoryMap';
import { getCategoryChain } from '../lib/getCategoryChain';

/*
 * Функция запроса за категориями по идентификаторам
 * @param queueToFetch
 */
async function fetchIds(queueToFetch: Set<string>) {
    const fetchedCategories: OpenApiSchemaCategory[] = [];
    const failedCategories: string[] = [];

    for (const id of queueToFetch) {
        try {
            const category = await api.endpoints.getById.query(id);

            fetchedCategories.push(category);
        } catch (e) {
            failedCategories.push(id);
        }
    }

    return { fetchedCategories, failedCategories };
}

type Returns = {
    /** Статусы получения всех цепочек категорий */
    isSuccess: boolean;
    isFetching: boolean;
    isError: boolean;
    /**
     * Результат выполнения функции (массив цепочек категорий для каждого переданного идентификатора)
     * Цепочка - массив категорий от родителя к потомку (переданной категории)
     * строка - нет данных по категории
     * объект - экземпляр категории
     */
    data: Array<string | OpenApiSchemaCategory>[];
    /** Функция перезапроса при ошибке */
    refetch: () => void;
};

/*
 * Хук для получения цепочки категорий ([Родитель/Потомок])
 * @param categoryIds идентификаторы категорий
 * @returns цепочка категорий для каждого переданного идентификатора
 */
export function useCategoriesChain(categoryIds: string[]): Returns {
    /** Запрос за всеми категориями */
    const { isSuccess, isFetching, isError, data, status } = api.endpoints.getAll.getCachedValue({});

    /** Дополнительные категории для добавления к маппингу */
    const [additionalCategories, setAdditionalCategories] = useState<OpenApiSchemaCategory[]>([]);
    /** Список категорий, которые не удалось получить */
    const [failedIds, setFailedIds] = useState<string[]>([]);

    /** Маппинг категорий */
    const mapCategories = useMemo(
        () => createCategoryMap([...(data ?? []), ...additionalCategories]),
        [additionalCategories, data]
    );

    /** Статусы расчета текущего выполнения хука */
    const refStatus = useRef({ isFetching, isError });
    /** Дополнительные категории для добавления к маппингу после текущего выполнения хука */
    const refAddToAdditionalCategories = useRef<OpenApiSchemaCategory[]>([]);

    // eslint-disable-next-line react-hooks/refs -- Автоматический игнор в рамках DIRECT-288723
    refAddToAdditionalCategories.current = [];

    /** Список категорий, которые не удалось получить после текущего выполнения хука */
    const refAddToFailedIds = useRef<string[]>([]);

    // eslint-disable-next-line react-hooks/refs -- Автоматический игнор в рамках DIRECT-288723
    refAddToFailedIds.current = [];

    /** Очередь запросов к категориям */
    const { chains, queueToFetch } = useMemo(() => {
        /** Очередь запросов к категориям */
        const queueToFetch = new Set<string>();

        /** Если не было запроса за общим списком либо запрос за общим списком в процессе или упал с ошибкой */
        if (!isSuccess || isError || isFetching) {
            /** Возвращаем пустую очередь для подзагрузки и массив цепочек из аргументов */
            return { queueToFetch, chains: categoryIds.map((id) => [id]) };
        }

        /** Цепочки категорий */
        const chains: Array<string | OpenApiSchemaCategory>[] = categoryIds.map<Array<string | OpenApiSchemaCategory>>(
            // eslint-disable-next-line react-hooks/refs -- Автоматический игнор в рамках DIRECT-288723
            (id) => {
                /** Цепочка родительских категорий для текущей категории */
                const chain: Array<string | OpenApiSchemaCategory> = getCategoryChain(id, { mapCategories });

                return chain.map((category) => {
                    if (typeof category === 'string') {
                        /** Если в цепочке строка айди, а не категория*/
                        /** Проверяем данные в сторе по этому айди */
                        const { status, data } = api.endpoints.getById.getCachedValue(category);

                        if (status === QueryStatus.uninitialized) {
                            /** Если запрос не был проинициализирован */
                            /** Ставим статус текущего выполнения хука в "Загрузка" */
                            refStatus.current.isFetching = true;
                            /** Добавляем айди категории для загрузки */
                            queueToFetch.add(category);
                        } else if (status === QueryStatus.fulfilled && data) {
                            /** Если запрос был выполнен успешно и данные есть */
                            /** Добавляем категорию в массив дополнительных категорий */
                            refAddToAdditionalCategories.current.push(data);

                            return data;
                        } else if (status === QueryStatus.rejected) {
                            /** Если запрос был выполнен с ошибкой */
                            /** Ставим статус текущего выполнения хука в "Ошибка" */
                            refStatus.current.isError = true;
                            refAddToFailedIds.current.push(category);

                            return category;
                        }
                    }

                    return category;
                });
            }
        );

        return {
            queueToFetch,
            chains,
        };
    }, [categoryIds, isError, isFetching, isSuccess, mapCategories]);

    const refAsync = useCheckAsync();

    useEffect(() => {
        if (status === QueryStatus.uninitialized) {
            /** Если запрос за всем списком категорий не был проинициализирован */
            /** Инициализируем запрос за всем списком категорий */
            api.endpoints.getAll.query({});
        } else if (queueToFetch.size > 0) {
            /** Если запрос за всем списком категорий был проинициализирован */
            fetchIds(queueToFetch).then(({ failedCategories, fetchedCategories }) => {
                if (refAsync.current.isMount) {
                    setAdditionalCategories((prevState) => [...prevState, ...fetchedCategories]);
                    setFailedIds((prevState) => [...prevState, ...failedCategories]);
                }
            });
        }

        if (refAddToAdditionalCategories.current.length) {
            setAdditionalCategories((prevState) => [...prevState, ...refAddToAdditionalCategories.current]);
        }

        if (refAddToFailedIds.current.length) {
            setFailedIds((prevState) => [...prevState, ...refAddToFailedIds.current]);
        }
    }, [queueToFetch, refAsync, status]);

    const refetch = useCallback(() => {
        setFailedIds([]);
        fetchIds(new Set(failedIds)).then(({ failedCategories, fetchedCategories }) => {
            if (refAsync.current.isMount) {
                setAdditionalCategories((prevState) => [...prevState, ...fetchedCategories]);
                setFailedIds((prevState) => [...prevState, ...failedCategories]);
            }
        });
    }, [failedIds, refAsync]);

    // eslint-disable-next-line react-hooks/refs -- Автоматический игнор в рамках DIRECT-288723
    return {
        // eslint-disable-next-line react-hooks/refs -- Автоматический игнор в рамках DIRECT-288723
        isSuccess: isSuccess && !refStatus.current.isError && !refStatus.current.isFetching && failedIds.length === 0,

        // eslint-disable-next-line react-hooks/refs -- Автоматический игнор в рамках DIRECT-288723
        isFetching: refStatus.current.isFetching && !refStatus.current.isError && failedIds.length === 0,

        // eslint-disable-next-line react-hooks/refs -- Автоматический игнор в рамках DIRECT-288723
        isError: refStatus.current.isError || failedIds.length > 0,
        data: chains,
        refetch,
    };
}
